import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Popover } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PopupDynamicFieldView } from 'constants/formBuilder';
import { FormSchemaType } from 'constants/forms';
import { checkIsTagAssignedDate, checkIsTagName, flatDynamicFields } from 'utils/forms';

import { selectDefaultPosConfigurationLogo } from 'store/companies/selectors';
import {
  selectDuplicateDynamicFieldNames,
  selectDynamicFieldsNamesById,
  selectDynamicFieldsSchema,
} from 'store/forms/selectors';

import DynamicFieldEditDeleteOptionsView from './dynamicField/DynamicFieldEditDeleteOptionsView';
import DynamicFieldHandWritingSignatureOptions from './dynamicField/DynamicFieldHandWritingSignatureOptions';
import DynamicFieldItemsList from './dynamicField/DynamicFieldItemsList/DynamicFieldItemsList';
import DynamicFieldNotaryOptionsView from './dynamicField/DynamicFieldNotaryOptionsView';
import RateCurrencyOptions from './dynamicField/DynamicFieldRateCurrencyOptions';
import DynamicFieldStyleOptionsView from './dynamicField/DynamicFieldStyleOptionsView';
import TodaysDatePlusNumberView from './dynamicField/DynamicFieldTodaysDatePlusNumberView';
import useEditorShim from './hooks/useEditorShim';
import {
  EMPTY_STYLE,
  IMAGE_DEFAULT_STYLE,
  PRISM_DEFAULT_FONT_FAMILY_OPTION,
} from './PrismFormEditor.pickers.constants';
import {
  dynamicEmptyPickerPaperProps,
  dynamicPickerPaperProps,
} from './PrismFormEditor.pickers.styles';
import {
  anchorPositionFromRect,
  getFontFamilyOptions,
  toPrismAttributes,
  withMeasuredLogoStyle,
} from './PrismFormEditor.pickers.utils';

const PrismDynamicFieldPicker = ({
  request,
  toAttributes = toPrismAttributes,
  hasCkeditorFontFamilies = false,
  newFieldStyle = EMPTY_STYLE,
}) => {
  const dynamicFieldsSchema = useSelector(selectDynamicFieldsSchema);
  const dynamicFieldsNamesById = useSelector(selectDynamicFieldsNamesById);
  const duplicateDynamicFieldNames = useSelector(selectDuplicateDynamicFieldNames);
  const formSettingsLogo = useSelector(selectDefaultPosConfigurationLogo);

  const dynamicFieldConfig = useMemo(
    () => ({ dynamicFieldsSchema, dynamicFieldsNamesById }),
    [dynamicFieldsSchema, dynamicFieldsNamesById],
  );

  const attrs = useMemo(() => request.attributes || {}, [request.attributes]);

  const initialType = attrs.dataDynamicType || '';
  const initialValue = attrs.dataDynamicValue || attrs.dataDynamicSlug || '';
  const initialTitle = attrs.dataDynamicTitle || '';
  const initialLabel = attrs.dataDynamicLabel || '';
  const isNewItem = !initialType;
  const initialView = initialType
    ? PopupDynamicFieldView.EDIT_DELETE_OPTIONS_VIEW
    : PopupDynamicFieldView.FIELDS_LIST_VIEW;

  const [view, setView] = useState(initialView);
  const [chosenOption, setChosenOption] = useState([
    initialTitle,
    initialValue,
    initialType,
    initialLabel,
  ]);

  const [savedWithLabel, setSavedWithLabel] = useState(
    _.isBoolean(attrs.dataDynamicWithLabel) ? attrs.dataDynamicWithLabel : true,
  );
  const [savedPoaVerbiage, setSavedPoaVerbiage] = useState(
    _.isBoolean(attrs.dataDynamicUsePoaVerbiage) ? attrs.dataDynamicUsePoaVerbiage : true,
  );

  const anchorPosition = useMemo(
    () => anchorPositionFromRect(request.anchorRect),
    [request.anchorRect],
  );

  const fieldsMap = useMemo(
    () => (dynamicFieldsSchema ? flatDynamicFields(dynamicFieldsSchema) : {}),
    [dynamicFieldsSchema],
  );

  const [chosenTitle, chosenValue, chosenType] = chosenOption;
  const isTagName = checkIsTagName(chosenTitle);
  const isTagAssignedDate = checkIsTagAssignedDate(chosenTitle);
  const savedIsTag = isTagName || isTagAssignedDate;

  const { isAvailablePostfix, isAvailablePrefix, isAvailableSpelledOut, schemaDefaultAction } =
    useMemo(() => {
      const fieldValue = chosenValue || initialValue;

      if (!fieldValue) {
        return {
          isAvailablePostfix: false,
          isAvailablePrefix: false,
          isAvailableSpelledOut: false,
          schemaDefaultAction: undefined,
        };
      }

      const field =
        fieldsMap[fieldValue] ||
        fieldsMap[fieldValue.toLowerCase?.()] ||
        fieldsMap[fieldValue.toUpperCase?.()];

      return {
        isAvailablePostfix: !!field?.isAvailablePostfix,
        isAvailablePrefix: !!field?.isAvailablePrefix,
        isAvailableSpelledOut: !!field?.isAvailableSpelledOut,
        schemaDefaultAction: field?.defaultAction,
      };
    }, [fieldsMap, chosenValue, initialValue]);

  const commit = useCallback(
    async pick => {
      if (pick?.type === FormSchemaType.BOOL && pick.withLabel !== undefined) {
        setSavedWithLabel(pick.withLabel);
      }

      if (pick?.type === FormSchemaType.BLANK_SIGNATURE_LINE && !_.isUndefined(pick.poaVerbiage)) {
        setSavedPoaVerbiage(pick.poaVerbiage);
      }

      const nextAttrs = toAttributes({
        pick: await withMeasuredLogoStyle(pick, formSettingsLogo),
        savedIsTag,
        sourceAttributes: attrs,
        dynamicFieldsSchema,
        duplicateDynamicFieldNames,
      });
      request.onSave(nextAttrs);
    },
    [
      request,
      attrs,
      savedIsTag,
      dynamicFieldsSchema,
      duplicateDynamicFieldNames,
      formSettingsLogo,
      toAttributes,
    ],
  );

  const onDelete = useCallback(() => {
    request.onDelete?.();
  }, [request]);

  const editorShim = useEditorShim({
    request,
    dynamicFieldConfig,
    onInsert: commit,
    onDelete,
  });

  const onClose = useCallback(() => {
    request.onClose?.();
  }, [request]);

  const onSetChosenOption = useCallback(next => setChosenOption(next), []);
  const onSetParentView = useCallback(viewId => setView(viewId), []);

  const onEditDeleteSetParentView = useCallback(
    viewId => {
      if (viewId === null) {
        onClose();

        return;
      }

      setView(viewId);
    },
    [onClose],
  );

  const dynamicStyle = attrs.dataDynamicStyle || newFieldStyle;
  const styleViewStyle = useMemo(
    () =>
      chosenType === FormSchemaType.IMAGE
        ? { ...IMAGE_DEFAULT_STYLE, ...dynamicStyle }
        : dynamicStyle,
    [chosenType, dynamicStyle],
  );
  const fontFamilyOptions = useMemo(
    () => (hasCkeditorFontFamilies ? null : getFontFamilyOptions(dynamicStyle.fontFamily)),
    [hasCkeditorFontFamilies, dynamicStyle.fontFamily],
  );
  const defaultFontFamilyOption = hasCkeditorFontFamilies ? null : PRISM_DEFAULT_FONT_FAMILY_OPTION;

  const popupContent = useMemo(() => {
    switch (view) {
      case PopupDynamicFieldView.FIELDS_LIST_VIEW:
        return (
          <DynamicFieldItemsList
            chosenOption={chosenOption}
            dynamicFieldConfig={dynamicFieldConfig}
            setChosenOption={onSetChosenOption}
            setParentView={onSetParentView}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.STYLE_OPTIONS_VIEW:
        return (
          <DynamicFieldStyleOptionsView
            action={attrs.dataDynamicAction}
            checkboxRenderType={attrs.dataDynamicCheckboxRenderType}
            chosenOption={chosenOption}
            dataDynamicMultiplier={attrs.dataDynamicMultiplier}
            dataDynamicPostfix={attrs.dataDynamicPostfix}
            dataDynamicPrefix={attrs.dataDynamicPrefix}
            defaultFontFamilyOption={defaultFontFamilyOption}
            editor={editorShim}
            fontFamilyOptions={fontFamilyOptions}
            handleClose={onClose}
            isAvailablePostfix={isAvailablePostfix}
            isAvailablePrefix={isAvailablePrefix}
            isAvailableSpelledOut={isAvailableSpelledOut}
            isNewItem={isNewItem}
            poaVerbiage={savedPoaVerbiage}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={onSetParentView}
            style={styleViewStyle}
            withLabel={savedWithLabel}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.TODAYS_DATE_PLUS_NUMBER:
        return (
          <TodaysDatePlusNumberView
            action={attrs.dataDynamicAction}
            chosenOption={chosenOption}
            editor={editorShim}
            handleClose={onClose}
            isNewItem={isNewItem}
            setParentView={onSetParentView}
            style={dynamicStyle}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.RATE_CURRENCY_OPTIONS:
        return (
          <RateCurrencyOptions
            action={attrs.dataDynamicAction}
            chosenOption={chosenOption}
            dataDynamicMultiplier={attrs.dataDynamicMultiplier}
            dataDynamicPostfix={attrs.dataDynamicPostfix}
            dataDynamicPrefix={attrs.dataDynamicPrefix}
            editor={editorShim}
            handleClose={onClose}
            isAvailablePostfix={isAvailablePostfix}
            isAvailablePrefix={isAvailablePrefix}
            isNewItem={isNewItem}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={onSetParentView}
            style={dynamicStyle}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.HAND_WRITING_SIGNATURE_OPTIONS:
        return (
          <DynamicFieldHandWritingSignatureOptions
            action={attrs.dataDynamicAction}
            chosenOption={chosenOption}
            editor={editorShim}
            handleClose={onClose}
            isNewItem={isNewItem}
            setParentView={onSetParentView}
            style={dynamicStyle}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.NOTARY_OPTIONS_VIEW:
        return (
          <DynamicFieldNotaryOptionsView
            action={attrs.dataDynamicAction}
            chosenOption={chosenOption}
            editor={editorShim}
            handleClose={onClose}
            isNewItem={isNewItem}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={onSetParentView}
            style={dynamicStyle}
            onSave={commit}
          />
        );

      case PopupDynamicFieldView.EDIT_DELETE_OPTIONS_VIEW:
        return (
          <DynamicFieldEditDeleteOptionsView
            dataDynamicLabel={attrs.dataDynamicLabel}
            dataDynamicTitle={attrs.dataDynamicTitle}
            editor={editorShim}
            ignoreUserDefinedStyles={!!attrs.dataDynamicIgnoreUserDefinedStyles}
            setParentView={onEditDeleteSetParentView}
            type={chosenType}
            value={chosenValue}
          />
        );

      default:
        return null;
    }
  }, [
    view,
    chosenOption,
    chosenType,
    chosenValue,
    dynamicFieldConfig,
    editorShim,
    attrs,
    dynamicStyle,
    styleViewStyle,
    fontFamilyOptions,
    defaultFontFamilyOption,
    isAvailablePostfix,
    isAvailablePrefix,
    isAvailableSpelledOut,
    isNewItem,
    savedPoaVerbiage,
    savedWithLabel,
    schemaDefaultAction,
    commit,
    onClose,
    onEditDeleteSetParentView,
    onSetChosenOption,
    onSetParentView,
  ]);

  if (!dynamicFieldsSchema || dynamicFieldsSchema.length === 0) {
    return (
      <Popover
        open
        PaperProps={dynamicEmptyPickerPaperProps}
        anchorPosition={anchorPosition}
        anchorReference="anchorPosition"
        onClose={request.onClose}
      >
        <Box>
          Dynamic fields schema not loaded yet. Open a Form Builder page first so AdminPanel can
          hydrate the store, then try again.
        </Box>
      </Popover>
    );
  }

  return (
    <Popover
      open
      PaperProps={dynamicPickerPaperProps}
      anchorPosition={anchorPosition}
      anchorReference="anchorPosition"
      onClose={request.onClose}
    >
      {popupContent}
    </Popover>
  );
};

PrismDynamicFieldPicker.propTypes = {
  request: PropTypes.object.isRequired,
  toAttributes: PropTypes.func,
  hasCkeditorFontFamilies: PropTypes.bool,
  newFieldStyle: PropTypes.object,
};

export default PrismDynamicFieldPicker;
